// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { useAuth } from '../AuthContext';

const DEMO_INVOICES = [
  { id: 'INV-2026-001', client: 'Acme Corp', email: 'billing@acme.com', amount: 4500, paid: 4500, status: 'Paid', issued: 'Feb 1, 2026', due: 'Feb 15, 2026', paidDate: 'Feb 12, 2026', items: [{ desc: 'Website Design Package', qty: 1, rate: 3500 }, { desc: 'SEO Setup', qty: 1, rate: 1000 }] },
  { id: 'INV-2026-002', client: 'TechStart Inc', email: 'ap@techstart.io', amount: 2800, paid: 0, status: 'Overdue', issued: 'Jan 20, 2026', due: 'Feb 3, 2026', paidDate: null, items: [{ desc: 'Mobile App Development - Phase 1', qty: 1, rate: 2800 }] },
  { id: 'INV-2026-003', client: 'Green Valley Farm', email: 'admin@greenvalley.ca', amount: 1200, paid: 600, status: 'Partial', issued: 'Feb 10, 2026', due: 'Feb 24, 2026', paidDate: null, items: [{ desc: 'Monthly Maintenance', qty: 1, rate: 800 }, { desc: 'SSL Certificate', qty: 1, rate: 200 }, { desc: 'Hosting (Annual)', qty: 1, rate: 200 }] },
  { id: 'INV-2026-004', client: 'Metro Dental', email: 'office@metrodental.com', amount: 6200, paid: 0, status: 'Sent', issued: 'Feb 18, 2026', due: 'Mar 4, 2026', paidDate: null, items: [{ desc: 'Full Brand Package', qty: 1, rate: 4200 }, { desc: 'Ad Campaign Setup', qty: 1, rate: 2000 }] },
  { id: 'INV-2026-005', client: 'Summit Fitness', email: 'info@summitfit.com', amount: 950, paid: 0, status: 'Draft', issued: '', due: '', paidDate: null, items: [{ desc: 'Booking Widget Integration', qty: 1, rate: 950 }] },
];

const PAYMENT_METHODS = [
  { id: 'stripe', name: 'Stripe', icon: '💳', status: 'connected', desc: 'Cards, ACH, Apple Pay, Google Pay' },
  { id: 'paypal', name: 'PayPal', icon: '🅿️', status: 'not_connected', desc: 'PayPal, Venmo, Pay Later' },
  { id: 'square', name: 'Square', icon: '⬛', status: 'not_connected', desc: 'Cards, cash, invoicing' },
  { id: 'interac', name: 'Interac e-Transfer', icon: '🏦', status: 'connected', desc: 'Canadian bank transfers' },
];

const RECURRING = [
  { id: 1, client: 'Acme Corp', amount: 800, frequency: 'Monthly', next: 'Mar 1, 2026', status: 'Active' },
  { id: 2, client: 'Green Valley Farm', amount: 200, frequency: 'Quarterly', next: 'Apr 1, 2026', status: 'Active' },
  { id: 3, client: 'Metro Dental', amount: 1500, frequency: 'Monthly', next: 'Mar 15, 2026', status: 'Paused' },
];

const S = {
  page: { padding: '24px', maxWidth: 1200, margin: '0 auto' },
  header: { marginBottom: 24 },
  title: { fontSize: 28, fontWeight: 700, color: 'var(--text)', margin: 0 },
  subtitle: { fontSize: 14, color: '#999', marginTop: 4 },
  tabs: { display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' },
  tab: (active) => ({ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14, background: active ? 'var(--accent, #d4a843)' : 'var(--card, #1a1a2e)', color: active ? '#000' : 'var(--text)' }),
  card: { background: 'var(--card, #1a1a2e)', borderRadius: 12, padding: 20, marginBottom: 16, border: '1px solid var(--border, #2a2a4a)' },
  cardTitle: { fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 12 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 },
  stat: { background: 'var(--card, #1a1a2e)', borderRadius: 12, padding: 20, textAlign: 'center', border: '1px solid var(--border, #2a2a4a)' },
  statValue: { fontSize: 28, fontWeight: 800, color: 'var(--accent, #d4a843)' },
  statLabel: { fontSize: 12, color: '#888', marginTop: 4 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid var(--border, #2a2a4a)', color: '#999', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' },
  td: { padding: '10px 12px', borderBottom: '1px solid var(--border, #2a2a4a)', color: 'var(--text)' },
  badge: (color) => ({ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: `${color}22`, color }),
  btn: (primary) => ({ padding: '10px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14, background: primary ? 'var(--accent, #d4a843)' : 'var(--card, #1a1a2e)', color: primary ? '#000' : 'var(--text)', border: primary ? 'none' : '1px solid var(--border, #2a2a4a)' }),
  input: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border, #2a2a4a)', background: 'var(--bg, #0f0f23)', color: 'var(--text)', fontSize: 14, boxSizing: 'border-box' },
  statusColor: (s) => s === 'Paid' ? '#4caf50' : s === 'Overdue' ? '#f44336' : s === 'Partial' ? '#ff9800' : s === 'Sent' ? '#2196f3' : '#888',
};

function InvoicePaymentsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('invoices');
  const [filter, setFilter] = useState('all');
  const [creating, setCreating] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [newInvoice, setNewInvoice] = useState({ client: '', email: '', items: [{ desc: '', qty: 1, rate: 0 }], notes: '', due: 14 });
  const [taxRate, setTaxRate] = useState(5);
  const [currency, setCurrency] = useState('CAD');

  const totalRevenue = DEMO_INVOICES.reduce((s, inv) => s + inv.paid, 0);
  const totalOutstanding = DEMO_INVOICES.reduce((s, inv) => s + (inv.amount - inv.paid), 0);
  const overdueCount = DEMO_INVOICES.filter(i => i.status === 'Overdue').length;
  const filtered = filter === 'all' ? DEMO_INVOICES : DEMO_INVOICES.filter(i => i.status.toLowerCase() === filter);

  const addItem = () => setNewInvoice(p => ({ ...p, items: [...p.items, { desc: '', qty: 1, rate: 0 }] }));
  const removeItem = (idx) => setNewInvoice(p => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));
  const updateItem = (idx, field, value) => setNewInvoice(p => ({ ...p, items: p.items.map((item, i) => i === idx ? { ...item, [field]: value } : item) }));
  const invoiceSubtotal = newInvoice.items.reduce((s, item) => s + (item.qty * item.rate), 0);
  const invoiceTax = invoiceSubtotal * (taxRate / 100);
  const invoiceTotal = invoiceSubtotal + invoiceTax;

  const TABS = [
    { id: 'invoices', label: 'Invoices', icon: '📄' },
    { id: 'payments', label: 'Payments', icon: '💳' },
    { id: 'recurring', label: 'Recurring', icon: '🔄' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
  ];

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={S.title}>💰 Invoice & Payments</h1>
            <p style={S.subtitle}>Professional invoicing, payment processing, and financial tracking</p>
          </div>
          <button style={S.btn(true)} onClick={() => setCreating(true)}>+ New Invoice</button>
        </div>
      </div>

      <div style={S.tabs}>
        {TABS.map(t => (
          <button key={t.id} style={S.tab(tab === t.id)} onClick={() => setTab(t.id)}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div style={S.grid}>
        <div style={S.stat}>
          <div style={S.statValue}>${totalRevenue.toLocaleString()}</div>
          <div style={S.statLabel}>Total Revenue</div>
        </div>
        <div style={S.stat}>
          <div style={{ ...S.statValue, color: '#ff9800' }}>${totalOutstanding.toLocaleString()}</div>
          <div style={S.statLabel}>Outstanding</div>
        </div>
        <div style={S.stat}>
          <div style={{ ...S.statValue, color: '#f44336' }}>{overdueCount}</div>
          <div style={S.statLabel}>Overdue</div>
        </div>
        <div style={S.stat}>
          <div style={S.statValue}>{DEMO_INVOICES.length}</div>
          <div style={S.statLabel}>Total Invoices</div>
        </div>
      </div>

      {/* Create Invoice Modal */}
      {creating && (
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={S.cardTitle}>Create New Invoice</div>
            <button style={{ ...S.btn(false), padding: '4px 12px' }} onClick={() => setCreating(false)}>✕</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Client Name *</label>
              <input style={S.input} placeholder="Business or client name" value={newInvoice.client} onChange={e => setNewInvoice(p => ({ ...p, client: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Client Email *</label>
              <input style={S.input} placeholder="billing@client.com" value={newInvoice.email} onChange={e => setNewInvoice(p => ({ ...p, email: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Currency</label>
              <select style={S.input} value={currency} onChange={e => setCurrency(e.target.value)}>
                <option value="CAD">CAD $</option>
                <option value="USD">USD $</option>
                <option value="EUR">EUR €</option>
                <option value="GBP">GBP £</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Tax Rate (%)</label>
              <input style={S.input} type="number" value={taxRate} onChange={e => setTaxRate(Number(e.target.value))} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Payment Due (days)</label>
              <select style={S.input} value={newInvoice.due} onChange={e => setNewInvoice(p => ({ ...p, due: Number(e.target.value) }))}>
                <option value={7}>Net 7</option>
                <option value={14}>Net 14</option>
                <option value={30}>Net 30</option>
                <option value={60}>Net 60</option>
                <option value={0}>Due on Receipt</option>
              </select>
            </div>
          </div>

          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>Line Items</div>
          {newInvoice.items.map((item, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr auto', gap: 8, marginBottom: 8 }}>
              <input style={S.input} placeholder="Description" value={item.desc} onChange={e => updateItem(i, 'desc', e.target.value)} />
              <input style={S.input} type="number" placeholder="Qty" value={item.qty} onChange={e => updateItem(i, 'qty', Number(e.target.value))} />
              <input style={S.input} type="number" placeholder="Rate" value={item.rate} onChange={e => updateItem(i, 'rate', Number(e.target.value))} />
              <button style={{ ...S.btn(false), padding: '4px 10px', color: '#f44336' }} onClick={() => removeItem(i)}>✕</button>
            </div>
          ))}
          <button style={{ ...S.btn(false), fontSize: 12, marginBottom: 16 }} onClick={addItem}>+ Add Line Item</button>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <div style={{ width: 250 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13, color: '#888' }}>
                <span>Subtotal</span><span>${invoiceSubtotal.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13, color: '#888' }}>
                <span>Tax ({taxRate}%)</span><span>${invoiceTax.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: 16, fontWeight: 700, color: 'var(--text)', borderTop: '2px solid var(--border, #2a2a4a)' }}>
                <span>Total</span><span>${invoiceTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Notes / Terms</label>
            <textarea style={{ ...S.input, minHeight: 60, resize: 'vertical' }} placeholder="Payment terms, thank you message, etc." value={newInvoice.notes} onChange={e => setNewInvoice(p => ({ ...p, notes: e.target.value }))} />
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <button style={S.btn(true)}>📧 Send Invoice</button>
            <button style={S.btn(false)}>💾 Save Draft</button>
            <button style={S.btn(false)}>📥 Download PDF</button>
          </div>
        </div>
      )}

      {/* Invoices Tab */}
      {tab === 'invoices' && (
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={S.cardTitle}>All Invoices</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {['all', 'paid', 'sent', 'overdue', 'draft'].map(f => (
                <button key={f} style={{ ...S.tab(filter === f), fontSize: 11, padding: '4px 12px' }} onClick={() => setFilter(f)}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Invoice #</th>
                  <th style={S.th}>Client</th>
                  <th style={S.th}>Amount</th>
                  <th style={S.th}>Paid</th>
                  <th style={S.th}>Status</th>
                  <th style={S.th}>Issued</th>
                  <th style={S.th}>Due</th>
                  <th style={S.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(inv => (
                  <tr key={inv.id}>
                    <td style={{ ...S.td, fontWeight: 600 }}>{inv.id}</td>
                    <td style={S.td}>{inv.client}</td>
                    <td style={{ ...S.td, fontWeight: 600 }}>${inv.amount.toLocaleString()}</td>
                    <td style={S.td}>${inv.paid.toLocaleString()}</td>
                    <td style={S.td}><span style={S.badge(S.statusColor(inv.status))}>{inv.status}</span></td>
                    <td style={S.td}>{inv.issued || '—'}</td>
                    <td style={S.td}>{inv.due || '—'}</td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button style={{ ...S.btn(false), padding: '2px 8px', fontSize: 11 }} onClick={() => setSelectedInvoice(inv)}>View</button>
                        <button style={{ ...S.btn(false), padding: '2px 8px', fontSize: 11 }}>📧</button>
                        <button style={{ ...S.btn(false), padding: '2px 8px', fontSize: 11 }}>📥</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invoice Detail */}
      {selectedInvoice && (
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={S.cardTitle}>{selectedInvoice.id} — {selectedInvoice.client}</div>
            <button style={{ ...S.btn(false), padding: '4px 12px' }} onClick={() => setSelectedInvoice(null)}>✕ Close</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div><span style={{ color: '#888', fontSize: 12 }}>Client:</span> <span style={{ color: 'var(--text)', fontWeight: 600 }}>{selectedInvoice.client}</span></div>
            <div><span style={{ color: '#888', fontSize: 12 }}>Email:</span> <span style={{ color: 'var(--text)' }}>{selectedInvoice.email}</span></div>
            <div><span style={{ color: '#888', fontSize: 12 }}>Issued:</span> <span style={{ color: 'var(--text)' }}>{selectedInvoice.issued || 'Not yet'}</span></div>
            <div><span style={{ color: '#888', fontSize: 12 }}>Due:</span> <span style={{ color: 'var(--text)' }}>{selectedInvoice.due || 'Not set'}</span></div>
          </div>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Description</th>
                <th style={S.th}>Qty</th>
                <th style={S.th}>Rate</th>
                <th style={S.th}>Total</th>
              </tr>
            </thead>
            <tbody>
              {selectedInvoice.items.map((item, i) => (
                <tr key={i}>
                  <td style={S.td}>{item.desc}</td>
                  <td style={S.td}>{item.qty}</td>
                  <td style={S.td}>${item.rate.toLocaleString()}</td>
                  <td style={{ ...S.td, fontWeight: 600 }}>${(item.qty * item.rate).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--accent, #d4a843)' }}>Total: ${selectedInvoice.amount.toLocaleString()}</div>
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            {selectedInvoice.status !== 'Paid' && <button style={S.btn(true)}>✅ Record Payment</button>}
            <button style={S.btn(false)}>📧 Resend</button>
            <button style={S.btn(false)}>📥 Download PDF</button>
            {selectedInvoice.status === 'Overdue' && <button style={{ ...S.btn(false), color: '#f44336' }}>⚠️ Send Reminder</button>}
          </div>
        </div>
      )}

      {/* Payments Tab */}
      {tab === 'payments' && (
        <div>
          <div style={S.card}>
            <div style={S.cardTitle}>Payment Processors</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
              {PAYMENT_METHODS.map(pm => (
                <div key={pm.id} style={{ padding: 16, borderRadius: 10, border: '1px solid var(--border, #2a2a4a)', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 28 }}>{pm.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: 'var(--text)' }}>{pm.name}</div>
                    <div style={{ fontSize: 11, color: '#888' }}>{pm.desc}</div>
                  </div>
                  <span style={S.badge(pm.status === 'connected' ? '#4caf50' : '#888')}>
                    {pm.status === 'connected' ? 'Connected' : 'Connect'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>Recent Payments</div>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Date</th>
                  <th style={S.th}>Client</th>
                  <th style={S.th}>Amount</th>
                  <th style={S.th}>Method</th>
                  <th style={S.th}>Invoice</th>
                  <th style={S.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { date: 'Feb 12, 2026', client: 'Acme Corp', amount: '$4,500', method: 'Stripe', invoice: 'INV-2026-001', status: 'Completed' },
                  { date: 'Feb 8, 2026', client: 'Green Valley Farm', amount: '$600', method: 'Interac', invoice: 'INV-2026-003', status: 'Completed' },
                  { date: 'Jan 30, 2026', client: 'Summit Fitness', amount: '$950', method: 'Stripe', invoice: 'INV-2025-048', status: 'Refunded' },
                ].map((p, i) => (
                  <tr key={i}>
                    <td style={S.td}>{p.date}</td>
                    <td style={{ ...S.td, fontWeight: 600 }}>{p.client}</td>
                    <td style={{ ...S.td, fontWeight: 600 }}>{p.amount}</td>
                    <td style={S.td}>{p.method}</td>
                    <td style={S.td}>{p.invoice}</td>
                    <td style={S.td}><span style={S.badge(p.status === 'Completed' ? '#4caf50' : '#f44336')}>{p.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>Payment Link Generator</div>
            <p style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>Create shareable payment links for quick invoice payments.</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
              <input style={S.input} placeholder="Amount" type="number" />
              <input style={S.input} placeholder="Description" />
              <select style={S.input}>
                <option>One-time</option>
                <option>Subscription</option>
              </select>
            </div>
            <button style={S.btn(true)}>🔗 Generate Payment Link</button>
          </div>
        </div>
      )}

      {/* Recurring Tab */}
      {tab === 'recurring' && (
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={S.cardTitle}>Recurring Invoices</div>
            <button style={S.btn(true)}>+ New Recurring</button>
          </div>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Client</th>
                <th style={S.th}>Amount</th>
                <th style={S.th}>Frequency</th>
                <th style={S.th}>Next Invoice</th>
                <th style={S.th}>Status</th>
                <th style={S.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {RECURRING.map(r => (
                <tr key={r.id}>
                  <td style={{ ...S.td, fontWeight: 600 }}>{r.client}</td>
                  <td style={{ ...S.td, fontWeight: 600 }}>${r.amount.toLocaleString()}/mo</td>
                  <td style={S.td}>{r.frequency}</td>
                  <td style={S.td}>{r.next}</td>
                  <td style={S.td}><span style={S.badge(r.status === 'Active' ? '#4caf50' : '#ff9800')}>{r.status}</span></td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={{ ...S.btn(false), padding: '2px 8px', fontSize: 11 }}>Edit</button>
                      <button style={{ ...S.btn(false), padding: '2px 8px', fontSize: 11 }}>{r.status === 'Active' ? 'Pause' : 'Resume'}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Settings Tab */}
      {tab === 'settings' && (
        <div>
          <div style={S.card}>
            <div style={S.cardTitle}>Invoice Settings</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Business Name</label>
                <input style={S.input} placeholder="Your Business Name" />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Business Email</label>
                <input style={S.input} placeholder="billing@yourbusiness.com" />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Business Address</label>
                <input style={S.input} placeholder="123 Main St, City, Province" />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Phone Number</label>
                <input style={S.input} placeholder="(555) 123-4567" />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Default Currency</label>
                <select style={S.input}><option>CAD</option><option>USD</option><option>EUR</option><option>GBP</option></select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Default Tax Rate (%)</label>
                <input style={S.input} type="number" placeholder="5" />
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Invoice Footer / Terms</label>
              <textarea style={{ ...S.input, minHeight: 60, resize: 'vertical' }} placeholder="Thank you for your business. Payment due within specified terms." />
            </div>
            <button style={S.btn(true)}>💾 Save Settings</button>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>Email Templates</div>
            {[
              { name: 'Invoice Sent', desc: 'Email sent when invoice is first issued' },
              { name: 'Payment Reminder', desc: 'Sent 3 days before due date' },
              { name: 'Overdue Notice', desc: 'Sent when invoice passes due date' },
              { name: 'Payment Received', desc: 'Thank you email after payment' },
            ].map((t, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i < 3 ? '1px solid var(--border, #2a2a4a)' : 'none' }}>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 14 }}>{t.name}</div>
                  <div style={{ fontSize: 12, color: '#888' }}>{t.desc}</div>
                </div>
                <button style={{ ...S.btn(false), fontSize: 12 }}>Edit Template</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default InvoicePaymentsPage;
